import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Calendar, Users, Clock, CalendarDays, CheckCircle2 } from 'lucide-react';
import { StatCard, WidgetPanel, RoundedBarChart } from './DashboardShared';
import { appointmentsOf, catalogServicesOf } from '../../../data/store';

export const AppointmentsModule = ({ org }) => {
  const metrics = useMemo(() => {
    const appointments = appointmentsOf(org.id) || [];
    const today = new Date().toDateString();
    
    // Today's stats
    const todayAppointments = appointments.filter(a => new Date(a.date).toDateString() === today);
    const completedToday = todayAppointments.filter(a => a.status === 'completed').length;
    
    // Upcoming
    const upcoming = appointments
      .filter(a => new Date(a.date) >= new Date() && a.status !== 'completed' && a.status !== 'cancelled')
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 5);

    // Total Bookings this month
    const thisMonth = appointments.filter(a => new Date(a.date).getMonth() === new Date().getMonth()).length;

    // Services breakdown
    const servicesMap = {};
    appointments.forEach(a => {
      (a.services || []).forEach(s => {
        servicesMap[s.name] = (servicesMap[s.name] || 0) + 1;
      });
    });

    const topServicesChartData = Object.entries(servicesMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([name, count]) => ({
        label: name.substring(0, 10) + (name.length > 10 ? '..' : ''),
        value: count
      }));

    return { 
      todayCount: todayAppointments.length, 
      completedToday, 
      upcoming, 
      thisMonth,
      topServicesChartData
    };
  }, [org.id]);

  return (
    <div className="flex flex-col gap-4 mt-6">
      <div className="flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.15em] text-[#0d0d0d] border-b border-[var(--theme-tertiary)] pb-2">
        <CalendarDays size={16} className="text-[var(--theme-primary)]" />
        Appointments & Bookings
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Calendar} label="Today's Bookings" value={metrics.todayCount} iconColor="#0ea5e9" trend={5} />
        <StatCard icon={CheckCircle2} label="Completed Today" value={metrics.completedToday} iconColor="#10b981" />
        <StatCard icon={CalendarDays} label="Bookings This Month" value={metrics.thisMonth} iconColor="#8b5cf6" trend={12} />
        <StatCard icon={Users} label="Active Staff" value={4} suffix="available" iconColor="#f59e0b" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <WidgetPanel title="Upcoming Appointments" className="lg:col-span-2">
          <div className="flex flex-col mt-2" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
            <div className="hidden sm:grid grid-cols-[1.5fr_1fr_1fr_100px] gap-4 px-4 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-100 mb-2">
              <div>Customer</div>
              <div>Date & Time</div>
              <div>Services</div>
              <div className="text-right">Status</div>
            </div>
            
            <div className="flex flex-col bg-white">
              {metrics.upcoming.length === 0 && (
                <div className="py-8 text-center text-[11px] font-medium text-slate-400 uppercase tracking-widest">
                  No upcoming bookings
                </div>
              )}
              {metrics.upcoming.map((a) => {
                const dateObj = new Date(a.date);
                const isConfirmed = a.status === 'confirmed';
                
                return (
                  <div key={a.id} className="flex flex-col sm:grid sm:grid-cols-[1.5fr_1fr_1fr_100px] gap-2 sm:gap-4 px-4 py-3 sm:items-center hover:bg-slate-50/50 transition-colors cursor-pointer group">
                    <div className="flex justify-between sm:contents">
                      <div className="order-1 sm:order-1 overflow-hidden flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-bold text-[10px] shrink-0">
                          {(a.customerName || 'W').charAt(0).toUpperCase()}
                        </div>
                        <span className="font-semibold text-[13px] text-slate-900 block truncate" title={a.customerName}>
                          {a.customerName || 'Walk-in Customer'}
                        </span>
                      </div>
                      
                      <div className="flex justify-end order-2 sm:order-4 sm:ml-auto">
                        <span className={`text-[10px] font-bold uppercase tracking-widest ${isConfirmed ? 'text-emerald-600' : 'text-amber-500'}`}>
                          {a.status}
                        </span>
                      </div>
                    </div>

                    <div className="text-[12px] font-medium text-slate-500 order-3 sm:order-2">
                      {dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, {a.time}
                    </div>

                    <div className="font-semibold text-[13px] text-slate-700 order-4 sm:order-3">
                      {a.services?.length || 1} Service(s)
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </WidgetPanel>

        <WidgetPanel title="Popular Services">
          <div className="flex flex-col gap-4 mt-2 h-full min-h-[200px]">
             {metrics.topServicesChartData.length === 0 && <p className="text-center text-xs text-slate-400 italic">No data</p>}
             {metrics.topServicesChartData.length > 0 && (
               <RoundedBarChart data={metrics.topServicesChartData} color="#0ea5e9" />
             )}
          </div>
        </WidgetPanel>
      </div>
    </div>
  );
};

AppointmentsModule.propTypes = {
  org: PropTypes.object.isRequired,
};
